import React, { useState, useEffect } from "react";
import { Button, Card, Col, Container, Form, Row } from "react-bootstrap";
import { Link, useParams, useNavigate, useLocation } from "react-router-dom";
import moment from "moment";
import Loader from "../../../../components/Common/Loader";
import Slider from "react-slick";
import {
  DELETEPRICE,
  ROOMLIST,
  ROOMPRICING,
  ROOMTYPE,
} from "../../../../services/ApiCalls";
import { useFieldArray, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

const roomSchema = z.object({
  roomId: z.string().min(1, { message: "Please select a room type" }),
  ratePricing: z.array(
    z.object({
      durationType: z.string().min(1, { message: "Duration type required" }),
      basePrice: z
        .number()
        .min(0, { message: "Base price cannot be negative" }),
      discount: z
        .number()
        .min(0, { message: "Discount cannot be negative" })
        .max(100, { message: "Discount cannot exceed 100%" }),
      refundable: z.boolean(),
    })
  ),
});

const RoomDetail = () => {
  const [roomDetail, setRoomDetail] = useState();
  const [loader, setLoader] = useState(false);
      const location = useLocation();
    
      const queryParams = new URLSearchParams(location.search);
      const page = queryParams.get("page") || 1;
  const [hotelAdminData, setHotelAdminData] = useState([]);
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(roomSchema),
    defaultValues: {
      roomId: "",
      ratePricing: [
        { durationType: "", basePrice: 0, discount: 0, refundable: true },
      ],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "ratePricing",
  });

  const getAmenityData = async () => {
    try {
      const roomRes = await ROOMTYPE({ limit: 100, page: 1 });
      if (roomRes?.data?.status === "success") {
        setHotelAdminData(roomRes?.data?.data);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const getData = async () => {
    try {
      setLoader(true);
      const res = await ROOMLIST({ id });
      if (res?.data?.status === "success") {
        setRoomDetail(res.data?.data);
      }
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    getAmenityData();
    if (id) getData();
  }, [id]);

  const submitHandler = async (data) => {
    setLoader(true);
    const payload = {
      ...data,
      roomId: id,
    };
    try {
      const res = await ROOMPRICING(payload);
      if (res?.data?.status === "success") {
        toast.success(res?.data?.message);
    
      } else {
        toast.error(res?.data?.message);
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoader(false);
    }
  };

  const handleDeleteRate = async (data) => {
    const payload = {
      id: id,
      priceId: data,
    };

    let res = await DELETEPRICE(payload);
    if (res?.data?.status === "success") {
      toast.success(res?.data?.message);
      getData();
    } else {
      toast.error(res?.data?.message);
    }
  };

  const handleCancel = () => navigate("/room");

  return (
    <>
      {loader && <Loader />}
      <section className="roomDetail py-3 position-relative">
        <Container>
          <Row>
            <Col lg="12">
              <div className="d-flex align-items-center gap-2">
                <Link
                  to={`/room?page=${page}`}
                  className="border d-flex align-items-center p-2 rounded"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="12"
                    height="12"
                    viewBox="0 0 10 16"
                    fill="none"
                  >
                    <path
                      d="M8.64707 0.473344C8.55514 0.381188 8.44594 0.308072 8.32572 0.258184C8.20549 0.208296 8.07661 0.182617 7.94644 0.182617C7.81628 0.182617 7.68739 0.208296 7.56717 0.258184C7.44694 0.308072 7.33774 0.381188 7.24582 0.473344L0.667065 7.05209C0.593675 7.12533 0.53545 7.21233 0.495723 7.3081C0.455996 7.40387 0.435547 7.50654 0.435547 7.61022C0.435547 7.7139 0.455996 7.81657 0.495723 7.91234C0.53545 8.00811 0.593675 8.0951 0.667065 8.16834L7.24582 14.7471C7.63373 15.135 8.25915 15.135 8.64707 14.7471C9.03498 14.3592 9.03498 13.7338 8.64707 13.3458L2.9154 7.60626L8.65498 1.86668C9.03498 1.48668 9.03498 0.853344 8.64707 0.473344Z"
                      fill="#E8307A"
                      stroke="#E8307A"
                      strokeWidth="0.2"
                    />
                  </svg>
                </Link>
                <h4 className="mb-0 py-3 fw-bold text-dark text-capitalize">
                  {t("manageRoom.detailTitle")}
                </h4>
              </div>
            </Col>
             <Col lg="12" className="my-2">
              <Card className="p-3 shadow-sm rounded">
                <h5 className="fw-bold mb-3">
                  {t("manageHotel.Roomimages", "Room Images")}
                </h5>

                {roomDetail?.images?.length > 0 ? (
                  roomDetail.images.length === 1 ? (
                    <div className="p-2">
                      <img
                        src={roomDetail.images[0]}
                        alt="room-0"
                        className="rounded shadow-sm"
                        style={{
                          width: "100%",
                          height: 250,
                          objectFit: "cover",
                        }}
                      />
                    </div>
                  ) : (
                    <Slider
                      dots={true}
                      infinite={roomDetail.images.length > 1}
                      speed={500}
                      slidesToShow={Math.min(roomDetail.images.length, 2)}
                      slidesToScroll={1}
                      arrows={roomDetail.images.length > 1}
                      responsive={[
                        {
                          breakpoint: 1024,
                          settings: {
                            slidesToShow: Math.min(roomDetail.images.length, 3),
                          },
                        },
                        {
                          breakpoint: 768,
                          settings: {
                            slidesToShow: Math.min(roomDetail.images.length, 2),
                          },
                        },
                        { breakpoint: 480, settings: { slidesToShow: 1 } },
                      ]}
                    >
                      {roomDetail.images.map((src, i) => (
                        <div key={i} className="p-2">
                          <img
                            src={src}
                            alt={`room-${i}`}
                            className="rounded shadow-sm"
                            style={{
                              width: "100%",
                              height: 200,
                              objectFit: "cover",
                            }}
                          />
                        </div>
                      ))}
                    </Slider>
                  )
                ) : (
                  <p className="text-muted">
                    {t("common.noImages", "No Images")}
                  </p>
                )}
              </Card>
            </Col>


            <Col lg="12" className="my-2">
              <div
                className="formWrpper px-lg-5 p-md-4 p-3 rounded"
                style={{ background: "#EEEEEE" }}
              >
                {roomDetail ? (
                  <Row className="justify-content-between">
                    <Col md={6} className="my-2">
                      <ul className="list-unstyled ps-0 mb-0 notLastBorder pe-lg-3">
                        <li className="py-3 d-flex align-items-center gap-10">
                          <p className="m-0 themeBlue fw-sbold w-50">
                            {t("manageRoom.columns.hotelName")}:
                          </p>
                          <h6 className="m-0 text-muted fw-bold w-50">
                            {roomDetail?.hotelId?.hotelName}
                          </h6>
                        </li>
                        <li className="py-3 d-flex align-items-center gap-10">
                          <p className="m-0 themeBlue fw-sbold w-50">
                            {t("manageHotel.columns.hotelId")}:
                          </p>
                          <h6 className="m-0 text-muted fw-bold w-50">
                            {roomDetail?.hotelId?.hotelId}
                          </h6>
                        </li>
                        <li className="py-3 d-flex align-items-center gap-10">
                          <p className="m-0 themeBlue fw-sbold w-50">
                            {t("manageRoom.columns.floorNumber")}:
                          </p>
                          <h6 className="m-0 text-muted fw-bold w-50">
                            {roomDetail?.floorNumber}
                          </h6>
                        </li>
                        <li className="py-3 d-flex align-items-center gap-10">
                          <p className="m-0 themeBlue fw-sbold w-50">
                            {t("manageRoom.columns.roomNumber")}:
                          </p>
                          <h6 className="m-0 text-muted fw-bold w-50">
                            {roomDetail?.roomNumber}
                          </h6>
                        </li>
                        <li className="py-3 d-flex align-items-center gap-10">
                          <p className="m-0 themeBlue fw-sbold w-50">
                            {t("manageRoom.columns.roomType")}:
                          </p>
                          <h6 className="m-0 text-muted fw-bold w-50">
                            {roomDetail?.type?.name}
                          </h6>
                        </li>
                        <li className="py-3 d-flex align-items-center gap-10">
                          <p className="m-0 themeBlue fw-sbold w-50">
                            {t("manageRoom.columns.createdAt")}:
                          </p>
                          <h6 className="m-0 text-muted fw-bold w-50">
                            {moment(roomDetail?.createdAt).format("YYYY-MM-DD")}
                          </h6>
                        </li>
                        <li className="py-3 d-flex align-items-center gap-10">
                          <p className="m-0 themeBlue fw-sbold w-50">
                            {t("manageRoom.columns.status")}:
                          </p>
                          <h6 className="m-0 text-muted fw-bold w-50">
                            <Form.Check
                              type="switch"
                              id="custom-switch"
                              checked={roomDetail?.status === "Available"}
                              readOnly
                            />
                          </h6>
                        </li>
                      </ul>
                    </Col>

                    <Col md={6} className="my-2">
                      <ul className="list-unstyled mb-0 notLastBorder ps-lg-3">
                        <li className="py-3 d-flex align-items-center gap-10">
                          <p className="m-0 themeBlue fw-sbold w-50">
                            {t("manageRoom.address")}:
                          </p>
                          <h6 className="m-0 text-muted fw-bold w-50">
                            {roomDetail?.hotelId?.address}
                          </h6>
                        </li>
                        <li className="py-3 d-flex align-items-center gap-10">
                          <p className="m-0 themeBlue fw-sbold w-50">
                            {t("manageRoom.updatedAt")}:
                          </p>
                          <h6 className="m-0 text-muted fw-bold w-50">
                            {moment(roomDetail?.updatedAt).format("YYYY-MM-DD")}
                          </h6>
                        </li>
                        <li className="py-3 d-flex align-items-center gap-10">
                          <p className="m-0 themeBlue fw-sbold w-50">
                            {t("manageRoom.beds")}:
                          </p>
                          <h6 className="m-0 text-muted fw-bold w-50">
                            {roomDetail?.numberOfBed}
                          </h6>
                        </li>
                        <li className="py-3 d-flex align-items-start gap-10">
                          <p className="m-0 themeBlue fw-sbold w-50">
                            {t("manageRoom.amenity")}:
                          </p>
                          <div className="m-0 text-muted w-50 d-flex flex-wrap gap-2">
                            {roomDetail?.amenities?.map((a) => (
                              <span key={a._id} className="badge bg-secondary">
                                {a.name}
                              </span>
                            ))}
                          </div>
                        </li>
                        <li className="py-3 d-flex align-items-start gap-10">
                          <p className="m-0 themeBlue fw-sbold w-50">
                            {t("manageRoom.notes")}:
                          </p>
                          <p
                            className="m-0 text-muted w-50"
                            style={{ fontSize: 12, lineHeight: "18px" }}
                          >
                            {roomDetail?.housekeepingNotes}
                          </p>
                        </li>
                      </ul>
                    </Col>

                    <Col lg="12" className="my-2">
                      <p className="m-0 themeBlue fw-sbold">
                        {t("manageRoom.rateprice")}
                      </p>
                      {roomDetail?.pricing?.length > 0 ? (
                        roomDetail.pricing.map((rate, index) => (
                          <div
                            key={rate._id || index}
                            className="border rounded p-2 mb-2 d-flex justify-content-between align-items-center"
                            style={{ fontSize: 13, lineHeight: "20px" }}
                          >
                            {/* Pricing Info */}
                            <div style={{ flex: 1 }}>
                              <Row>
                                <Col md={3}>
                                  <strong>{t("manageRoom.duration")}:</strong>{" "}
                                  {rate?.durationType}
                                </Col>
                                <Col md={2}>
                                  <strong>{t("manageRoom.base")}:</strong>{" "}
                                  {rate?.basePrice}
                                </Col>
                                <Col md={2}>
                                  <strong>{t("manageRoom.discount")}:</strong>{" "}
                                  {rate?.discount}%
                                </Col>
                                <Col md={2}>
                                  <strong className="text-nowrap">
                                    {t("manageRoom.final")}:
                                  </strong>{" "}
                                  {rate?.finalPrice.toFixed(2)}
                                </Col>
                                <Col md={2}>
                                  <strong>Date:</strong>{" "}
                                  {moment(rate?.date).format("YYYY-MM-DD")}
                                </Col>
                                <Col md={3}>
                                  <strong>{t("manageRoom.refundable")}:</strong>{" "}
                                  {rate?.refundable ? "Yes" : "No"}
                                </Col>
                              </Row>
                            </div>

                            {/* Delete Button */}
                            <div className="ms-2">
                              <Button
                                variant="danger"
                                size="sm"
                                onClick={() =>
                                  handleDeleteRate(rate._id || index)
                                }
                              >
                                {t("common.delete", "Delete")}
                              </Button>
                            </div>
                          </div>
                        ))
                      ) : (
                        <p className="text-muted" style={{ fontSize: 12 }}>
                          {t("manageRoom.norateProvide")}
                        </p>
                      )}
                    </Col>

                
                  </Row>
                ) : (
                  <p className="text-center text-muted">
                    No room details found
                  </p>
                )}
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </>
  );
};

export default RoomDetail;
